import classNames from "classnames";
import { ListGroup, ListGroupControl, ListGroupItem, LoadingState, View } from "components/Shared";
import { translate } from "lib";
import Datasource, { DatasourceResponse } from "lib/datasource";
import _ from "lodash";
import moment from "moment";
import React, { memo, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import StatedBase from "./StatedBase";
/**
 * The amendments on one lease, oldest first
 * (GET /v2/lease-agreements/:id/amendments, V2::AmendmentsController#index).
 * The DEFAULT tab: an amendment is the unit of work everything else on the lease
 * hangs off, so it is what the page opens on.
 *
 * Built on the shared `ListGroupItem`, same as the property's Tenancies tab —
 * including the teal `btn btn-primary` pencil Edit button in the last datapoint.
 *
 * TWO DESTINATIONS PER ROW, and they are different on purpose:
 *
 *   the row  -> the PROJECT the amendment created. That is the work: the
 *               workflow, the tasks, the documents. Most amendment types create
 *               no project at all (Amendment::TypeBehaviour); ListGroupItem
 *               renders those as a plain div with no chevron, because `link` is
 *               false. A PENDING project goes to the create wizard instead —
 *               see `projectLink` below.
 *   View     -> the AMENDMENT itself (AmendmentShow): a page with two tabs —
 *               Details, which states the record and carries its own Edit button,
 *               and Assets, read-only. It said "Edit" and went straight to the
 *               form until 26 Aug, when Sim added the Assets tab; the form is now
 *               one step further in, at `.../amendments/:amendmentId/edit`.
 *
 * The list is FLAT, ordered by start date, with each row naming the amendment it
 * branches from. The amendments of a lease are a tree (linked by Yardi's
 * BaseAmendmentID) and nothing here hides that — "branches from" is the link —
 * but a lease's history reads better as a sequence than as an outline.
 *
 * MIND THE TWO FAMILIES OF ID. `amendment.id` is TCP's uuid, which is what the
 * View link uses. `amendment.amendment_id` is YARDI's AmendmentID string, which
 * is what the Amendment ID column shows and what `base_amendment_id` points at.
 *
 * SORT AND SEARCH ARE CLIENT-SIDE HERE, unlike the lease list. The whole tree
 * arrives in one request (`per_page: 1000` — a lease has a handful of nodes and
 * paging its history would serve nobody), so filtering in place costs no request
 * and no debounce. That is the same trade components/Tenancies/Collection makes
 * for the same reason.
 */

interface Props {
	leaseAgreementId: string;
	name?: string;
	icon?: string;
}

const dash = (value: any) => (value === 0 || !!value ? value : "—");

// WHERE THE ROW GOES. `false`, not null, when the amendment created no project —
// ListGroupItem then renders a div with no href and no chevron instead of a dead
// link.
//
// A PENDING project HAS NO WORKSPACE TO OPEN (Sim, 28 Aug). `status: "pending"`
// means the project is a staged Yardi payload that nobody has finished creating:
// no workflow, no tasks, no documents, so `/projects/:id` shows an empty shell.
// It belongs in the create wizard, at the same "Scheduling" step the Pending
// Projects list continues at (`/projects/:id/schedule`, App.tsx -> CreateProject).
// The flip is server-side and one-way (V2::ProjectsController#finalize sets
// status back to null), so the link follows the payload and needs no local state.
//
// A CANCELLED amendment therefore does NOT go to the wizard, even when its
// project is still `status: "pending"` under the hood: `project_status` answers
// "cancelled" first (AmendmentSerializer), so the row falls through to
// `/projects/:id`. That is deliberate and matches the Pending Projects list,
// which no longer offers it either (Sim, 3 Sep 2026) — inviting someone to
// finish creating a project that has been called off is the bug. The workspace it
// opens can be an empty shell for a never-finalised payload, but it carries the
// red Cancelled tag that says why.
const projectLink = (amendment: tcpinpoint.Amendment) => {
	if (!amendment.project_id) return false;
	return amendment.project_status === "pending"
		? `/projects/${amendment.project_id}/schedule`
		: `/projects/${amendment.project_id}`;
};

// THE PROJECT STATUS COLUMN. `project_status` arrives already decided
// ("cancelled" | "pending" | "open" | "archived" | null — AmendmentSerializer), so
// this is only the badge class for each answer:
//
//   cancelled -> `badge-danger`, THE SAME RED as the Cancelled badge on the
//               heading of this very row (Sim, 3 Sep 2026). The two say one
//               thing, so they are one colour; the row already reads
//               `text-danger` for the same reason.
//   pending  -> `badge-attention`, the SAME yellow as the "Pending Projects"
//               button on the projects header (Sim, 28 Aug). One colour for one
//               thing: wherever a pending project appears it looks pending.
//   open     -> green
//   archived -> the plain grey badge, the default — same swatch as the Branch
//               badge on the heading.
//
// A null (no project) falls through to `dash`, matching the Project column
// beside it rather than inventing a fifth badge.
// EXPORTED (Sim, 3 Sep 2026) so the project show header's "Status:" row uses the
// SAME map. One colour per state wherever it appears was the point of the
// `badge-attention` yellow in the first place; two copies would drift.
export const PROJECT_STATUS_BADGES: { [status: string]: string } = {
	cancelled: "badge-danger",
	pending: "badge-attention",
	open: "badge-success",
	archived: ""
};

// Sortable in the browser, so these are not limited to real columns the way the
// lease list's are — but they must still be KEYS ON THE SERIALIZED AMENDMENT,
// because ListGroupControl uses each one both as the lodash sort path and as the
// translate() label.
// `amendment_id` LEADS AND IS THE DEFAULT (Sim, 24 Aug). Yardi issues AmendmentIDs
// in the order the amendments happened, so it is the closest thing the tree has to
// a canonical order — and unlike the start date every row has one.
const SORT_OPTIONS = ["amendment_id", "amendment_start_date", "amendment_type", "amendment_status"];

const sortValue = (amendment: tcpinpoint.Amendment, sortBy: string) => {
	// NUMERICALLY. `amendment_id` is Yardi's AmendmentID STRING, so a plain string
	// sort puts "9" after "10728826" — the ids are only ever read as numbers.
	// Anything that will not parse (Yardi's "NA", a blank) sorts LAST rather than
	// as 0, where it would sit above every real amendment.
	//
	// A SEED LEADS (Sim, 25 Sep 2026). Its id is TCP's own ("TCP-8745217"), which
	// will not parse, so it used to sort LAST — below the Renewal hanging off it.
	// A seed stands in for every AmendmentID the lease had before the migration,
	// so it is older than anything Yardi sends: the Seed Original Lease first,
	// then any Seed Expansion, then the numeric ids.
	if (sortBy === "amendment_id") {
		const type = _.toLower(_.trim(_.toString(amendment.amendment_type)));
		if (type === "seed original lease") return -2;
		if (type === "seed expansion") return -1;
		const numeric = Number(_.toString(amendment.amendment_id).trim());
		return Number.isFinite(numeric) ? numeric : Number.MAX_SAFE_INTEGER;
	}
	// A missing start date sorts FIRST, matching the API's `NULLS FIRST` ordering,
	// so switching sort back and forth does not reshuffle the undated rows.
	if (sortBy === "amendment_start_date")
		return !!amendment.amendment_start_date ? moment(amendment.amendment_start_date).valueOf() : 0;
	return _.toLower(_.toString(_.get(amendment, sortBy)));
};

// `spaced` because the places this is used need different gaps: after the
// heading text a badge needs to be pushed off the word before it, while the one
// in the Project Status column is alone in a centred datapoint and the margin
// would just knock it off centre.
//
// EXPORTED as `ProjectStatusBadge` (see the barrel) for the project show
// header's "Status:" row — the same markup and the same dictionary keys, so the
// two screens cannot disagree about what a state looks like.
export const Badge = ({
	label,
	className,
	spaced = true
}: {
	label: string;
	className?: string;
	spaced?: boolean;
}) => (
	<span className={classNames("badge", className)} style={spaced ? { marginLeft: "0.5rem" } : undefined}>
		{translate(label)}
	</span>
);

const AmendmentRow = ({
	amendment,
	leaseAgreementId
}: {
	amendment: tcpinpoint.Amendment;
	leaseAgreementId: string;
}) => (
	<ListGroupItem
		id={amendment.id}
		link={projectLink(amendment)}
		heading={
			<React.Fragment>
				<span className={classNames({ "text-danger": !!amendment.cancelled })}>
					{amendment.amendment_type || translate("unknown")}
				</span>
				{/* No "Current term" badge (Sim, 24 Aug). Cancelled is the badge people
				    come here to find: the node stays in the history, it just grants
				    nothing and its project is closed rather than deleted
				    (Amendment#cancel!). An Expansion and everything under it is a
				    BRANCH, never the main tree — Amendment#on_main_tree? decides that
				    server-side and it is not re-derived here. */}
				{!!amendment.cancelled && <Badge label="lease_agreement_cancelled" className="badge-danger" />}
				{/* Only when it actually branches OFF something (Sim, 25 Sep 2026): a
				    Seed Expansion with no base stands beside the trunk and is not a
				    branch of it. `branch` is Amendment#branch?. */}
				{!!amendment.branch && <Badge label="lease_agreement_branch" />}
			</React.Fragment>
		}
		// NO STATUS HERE (Sim, 24 Aug). The subheading is now the tree link and
		// nothing else: the status is a field of the amendment, corrected on the
		// Edit form, and it read as a second name for the row. It is still
		// sortable and searchable below, because "show me the In Process ones" is
		// a real question about a list you can no longer scan for it.
		// Yardi's own BaseAmendmentID, then the parent TCP resolved it to, muted
		// (Sim, 25 Sep 2026): "Branches from #949494 (TCP-8745217) Seed Original
		// Lease". See StatedBase.
		subheading={
			!!amendment.base_amendment_id ? (
				<React.Fragment>
					<StatedBase amendment={amendment} prefix={`${translate("amendment_branches_from")} #`} />
					{!!amendment.base_amendment_type ? ` ${amendment.base_amendment_type}` : ""}
				</React.Fragment>
			) : (
				""
			)
		}
		datapoints={[
			// A COLUMN, not a suffix on the heading (Sim, 24 Aug) — it is a field of
			// the amendment, not part of its name.
			{ key: "amendment_id", value: <React.Fragment>{dash(amendment.amendment_id)}</React.Fragment> },
			{ key: "project", value: <React.Fragment>{dash(amendment.project_name)}</React.Fragment> },
			{
				key: "project_status",
				value: (
					<React.Fragment>
						{!!amendment.project_status ? (
							<Badge
								label={amendment.project_status}
								className={PROJECT_STATUS_BADGES[amendment.project_status]}
								spaced={false}
							/>
						) : (
							dash(null)
						)}
					</React.Fragment>
				)
			},
			{
				key: "",
				value: (
					// stopPropagation, because when the row HAS a project ListGroupItem
					// wraps everything in a <Link>: without it the inner Link would
					// navigate to the amendment page and the click would then bubble to
					// the outer one, which would immediately replace it with the project.
					// eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
					<span onClick={event => event.stopPropagation()}>
						{/* "View", not "Edit", and an eye rather than a pencil (Sim,
						    26 Aug): the destination is the amendment's own page, whose
						    Details tab edits and whose Assets tab does not. Still the
						    teal `btn btn-primary` in the last datapoint — that is the
						    list-row convention and only the label and glyph changed. */}
						<Link className="btn btn-primary" to={`/lease-agreements/${leaseAgreementId}/amendments/${amendment.id}`}>
							<i className="fa fa-eye" /> {translate("view")}
						</Link>
					</span>
				)
			}
		]}
	/>
);

const AmendmentsTab = ({ leaseAgreementId }: Props) => {
	// null = no response yet. Driven from our own state rather than
	// `datasource.isLoading`: with no `renderTrigger` the datasource cannot
	// re-render this component when it flips isLoading back to false, which would
	// leave the tab stuck on <LoadingState />.
	const [amendments, setAmendments] = useState<tcpinpoint.Amendment[] | null>(null);
	const [sortBy, setSortBy] = useState("amendment_id");
	const [searchTerm, setSearchTerm] = useState("");
	const [datasource] = useState(new Datasource({ mainModelName: "amendment" }));

	useEffect(() => {
		let sub = datasource.responseSubject$.subscribe((response: DatasourceResponse) =>
			setAmendments(response.normalizedMainModelResponse || [])
		);
		return () => sub.unsubscribe();
	}, [datasource]);

	useEffect(() => {
		if (_.isEmpty(leaseAgreementId)) return;
		// per_page high enough to be one page: a lease's tree is a handful of nodes
		// and the list is the history, which paging would break up for no reason.
		datasource.get("v2", `lease-agreements/${leaseAgreementId}/amendments`, {
			queryStringParameters: { per_page: 1000 }
		});
	}, [datasource, leaseAgreementId]);

	if (amendments === null) return <LoadingState />;

	// Everything a row SHOWS is searchable, including the unit codes — "which
	// amendment brought MW001A in" is the question this list gets asked.
	const term = searchTerm.trim().toLowerCase();
	const matches = (amendment: tcpinpoint.Amendment) => {
		if (!term) return true;
		return _.some(
			[
				amendment.amendment_type,
				amendment.amendment_id,
				amendment.amendment_status,
				amendment.base_amendment_id,
				amendment.stated_base_amendment_id,
				amendment.base_amendment_type,
				amendment.project_name,
				_.join(amendment.unit_codes || [], " ")
			],
			field => _.toLower(_.toString(field)).includes(term)
		);
	};
	const collection = _.filter(
		_.sortBy(amendments, amendment => sortValue(amendment, sortBy)),
		matches
	);

	return (
		<View>
			<ListGroupControl
				onSortChange={setSortBy}
				selectedSort={sortBy}
				sortOptions={SORT_OPTIONS}
				onSearchChange={setSearchTerm}
				searchValue={searchTerm}
				searchLabel="search_amendments"
			/>
			{/* The empty state is ListGroup's — the shared <EmptyState/>, same as every
			    other list in the app. It covers both "this lease has no amendments" and
			    "nothing matches the search", which is why there is no branch here. */}
			<ListGroup
				resource="amendments"
				collection={collection}
				pagination={false}
				renderRow={(amendment: tcpinpoint.Amendment) => (
					<AmendmentRow key={amendment.id} amendment={amendment} leaseAgreementId={leaseAgreementId} />
				)}
			/>
		</View>
	);
};

export default memo(AmendmentsTab);
